import React, { useState, useEffect, useCallback, useContext, useMemo, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import {
  Result, Row, Col, Card, Tag, Typography, Button,
  Select, Empty, Dropdown, Drawer, Divider,
} from 'antd';
import {
  WhatsAppOutlined, EyeOutlined,
  DollarOutlined, FilterOutlined, DownOutlined, LoginOutlined, AppstoreOutlined, SearchOutlined,
  MenuOutlined, CloseOutlined, StarFilled, BankOutlined, PlayCircleOutlined,
} from '@ant-design/icons';
import { micrositioService, analyticsService, fixUrl } from '../../services/api';
import { getShareUrl } from '../../services/shareUrls';
import PujaSpinner from '../../components/PujaSpinner';
import CountdownPreOfertas from '../../components/CountdownPreOfertas';
import AuthContext from '../../context/AuthContext';
import { rutaAcceso } from '../../utils/rutasAcceso';
import { leerDeepLinkConsumido, marcarDeepLinkConsumido } from '../../utils/deepLink';
import { heroBackground, heroTextLayout, heroTextPadding, heroMargenSuperior } from '../../utils/hero';
import { tipografiaDe, altoHeaderDe, columnasValidas, terminoLote } from '../../utils/micrositioTema';
import { buildAnimalSlug, youtubeThumb, imagenAnimalUrl } from '../../utils/animalMedia';
import { urlExternaStreaming } from '../../utils/streaming';
import { useAvisarAltoAlPadre } from '../../hooks/useAvisarAltoAlPadre';
import useFaviconMicrositio from './useFaviconMicrositio';
import { urlSegura, abrirExterna } from '../../utils/urlSegura';

const { Title, Text } = Typography;
const { Option } = Select;

// Deep-link embebido: solo debe consumirse UNA vez, en la carga inicial.
// A nivel de módulo (no state/ref) para sobrevivir a los re-montajes de
// RematePage — si no, cada click en INICIO/Categorías vuelve a disparar el
// handshake y el parent (que conserva ?animal= en su URL) nos tira de vuelta
// a la ficha. Se resetea en un full page reload, que es justo cuando sí
// queremos re-aplicar el deep-link.
//
// PERO no todo full reload es el visitante volviendo a entrar: el countdown de
// pre-ofertas recarga SOLO cuando la ventana abre o cierra, y esa recarga
// rearmaba el deep-link. La marca vive en utils/deepLink.js — ver ahí el detalle.
let deepLinkConsumed = false;
let flagLeido = false;

// La marca se lee RECIÉN cuando el deep-link entra en juego, no al evaluar el
// módulo. Leerla es destructivo (se borra al leerla), así que hacerlo en el
// scope del módulo ataba el resultado a QUÉ archivo importa a este primero —
// y eso lo decide el bundler, no nosotros. Una garantía de corrección no puede
// depender del orden de evaluación de los módulos.
const asegurarFlagLeido = () => {
  if (flagLeido) return;
  flagLeido = true;
  deepLinkConsumed = leerDeepLinkConsumido();
};

// Sponsors: jerarquía visual por nivel. gold/main (Principal) > silver (Plata)
// > sponsor (Auspiciante). Define el tamaño del logo y el orden de aparición.
const SPONSOR_TITULOS = { gold: 'Sponsors Oro', silver: 'Sponsors Plata', sponsor: 'Auspiciantes' };
// Título por nivel: el personalizado del evento (config) o el default.
const tituloNivel = (config, nivel) =>
  (config?.[`sponsor_titulo_${nivel}`] || '').trim() || SPONSOR_TITULOS[nivel];
// Tamaños +40% respecto al diseño original; jerarquía Oro > Plata > Auspiciante.
const sponsorLogoSize = (tipo) =>
  (tipo === 'gold' || tipo === 'main') ? { maxHeight: 126, maxWidth: 266 }
    : tipo === 'silver' ? { maxHeight: 87, maxWidth: 182 }
    : { maxHeight: 62, maxWidth: 133 };
const nivelSponsor = (tipo) => (tipo === 'main' ? 'gold' : (tipo || 'sponsor'));

// Sección de sponsors agrupada por nivel, con título por grupo y efecto hover.
const SponsorsPorNivel = ({ sponsors, tipografia, config = {} }) => {
  if (!sponsors?.length) return null;
  const grupos = ['gold', 'silver', 'sponsor']
    .map(nivel => ({
      nivel,
      items: sponsors
        .filter(s => nivelSponsor(s.tipo_sponsor) === nivel)
        .sort((a, b) => (a.orden || 0) - (b.orden || 0)),
    }))
    .filter(g => g.items.length > 0);
  if (!grupos.length) return null;

  return (
    <div style={{ marginTop: 48, padding: '8px 0 16px', borderTop: '1px solid #f0f0f0' }}>
      <style>{`
        .puja-sponsor-logo { transition: transform 0.3s ease; cursor: pointer; }
        .puja-sponsor-logo:hover { transform: scale(1.07); }
      `}</style>
      {grupos.map(g => (
        <div key={g.nivel} style={{ marginTop: 32 }}>
          <div style={{
            fontWeight: 700, fontSize: 12, textTransform: 'uppercase', color: '#999',
            letterSpacing: 1.2, marginBottom: 20, textAlign: 'center', fontFamily: tipografia.body,
          }}>
            {tituloNivel(config, g.nivel)}
          </div>
          <Row justify="center" gutter={[40, 28]} align="middle">
            {g.items.map(s => (
              <Col key={s.id_sponsor}>
                {s.url_imagen ? (
                  <a
                    // `url_link` es texto LIBRE del sponsor:
                    // `sponsor_routes.py` lo guarda con `data.get('url_link')`
                    // sobre un `db.String(1024)`, sin validar nada. Un
                    // `javascript:` ahi se ejecuta en una pagina PUBLICA, a
                    // cada visitante que toque el logo.
                    //
                    // Sin URL segura queda `undefined` y NO `'#'`: un `<a>`
                    // sin href no es un link —no navega, no toma foco, el
                    // navegador no lo ofrece en "copiar direccion"— y el logo
                    // se sigue viendo igual. El `'#'` de antes era un link a
                    // ninguna parte que ademas ensuciaba la URL con un hash.
                    href={urlSegura(s.url_link) || undefined}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <img
                      className="puja-sponsor-logo"
                      src={fixUrl(s.url_imagen)}
                      alt={s.nombre_sponsor}
                      loading="lazy"
                      style={{ ...sponsorLogoSize(s.tipo_sponsor), objectFit: 'contain' }}
                    />
                  </a>
                ) : (
                  <Tag color={g.nivel === 'gold' ? 'gold' : 'default'} style={{ fontSize: 13 }}>
                    {s.nombre_sponsor}
                  </Tag>
                )}
              </Col>
            ))}
          </Row>
        </div>
      ))}
    </div>
  );
};

// Colapsa los corrales AGRUPADOS: deja UN representante por corral (el primero
// según el orden corral/nombre) con _cantidadLote = cabezas del corral. Los
// animales no agrupados quedan individuales.
//
// Se EXPORTA porque las páginas internas derivan de acá su lista de razas y
// cabañas para el menú: si una la sacara del listado crudo y el catálogo del
// colapsado, el menú podría ofrecer una cabaña que en el catálogo no existe
// —o al revés, aparecer en Contacto y desaparecer en el catálogo—.
export const colapsarAgrupados = (lista) => {
  const porCorral = {};
  lista.forEach(a => {
    if (a.corral != null) {
      if (!porCorral[a.corral]) porCorral[a.corral] = [];
      porCorral[a.corral].push(a);
    }
  });
  const vistos = new Set();
  const out = [];
  for (const a of lista) {
    if (a.agrupado && a.corral != null) {
      if (vistos.has(a.corral)) continue;
      vistos.add(a.corral);
      const lote = porCorral[a.corral];
      out.push({
        ...a,
        _cantidadLote: lote.length,
        // LA TARJETA REPRESENTA A TODO EL CORRAL, así que tiene que poder
        // encontrarse por cualquiera de sus animales. Sin esto, el buscador y
        // el filtro de padre miraban sólo al primero: en el corral 43 (RP 331,
        // 6367 y 6420) buscar "6367" no traía nada, porque ese animal no
        // existía en la lista colapsada (2026-10-15).
        _textoLote: lote.map(x => `${x.nom_animal || ''} ${x.rp_animal || ''}`).join(' ').toLowerCase(),
        _padresLote: [...new Set(lote.map(x => x.nom_padre).filter(Boolean))],
      });
    } else {
      out.push(a);
    }
  }
  return out;
};

// Lo que el buscador compara y los padres que un filtro puede matchear, para
// una tarjeta: los de todo el lote si es un corral colapsado, los propios si no.
const textoBuscable = (a) => a._textoLote ?? `${a.nom_animal || ''} ${a.rp_animal || ''}`.toLowerCase();
const padresDe = (a) => a._padresLote ?? (a.nom_padre ? [a.nom_padre] : []);

// Título de la tarjeta en el index. Un corral agrupado con más de un animal
// NO se titula con el nombre del primero: eso hacía creer que la tarjeta era
// de ese animal y no del lote (pedido del usuario, 2026-10-15). El corral y
// las cabezas siguen en el tag de abajo, igual que antes.
const tituloTarjeta = (a, config) =>
  (a.agrupado && a._cantidadLote > 1 ? `${terminoLote(config).nombre} Agrupado` : a.nom_animal);

// Helpers del hero: viven en utils/hero.js (los usa tambien el editor del
// panel). Se re-exportan aca porque las otras paginas del micrositio ya los
// importaban desde este modulo. El import es aparte del re-export: `export
// {...} from` NO trae los nombres al scope local, y este archivo los usa.
export {
  heroOverlayRgba, heroBgPosition, heroBgSize, heroBackground,
  heroTextLayout, heroTextPadding,
} from '../../utils/hero';

/**
 * Aviso de que el remate físico se está transmitiendo.
 *
 * El botón NAVEGA a una página nuestra, no al link del proveedor: el visitante
 * mira la transmisión sin salir del micrositio, y por lo tanto sigue a un click
 * del catálogo y de las ofertas. Mandarlo a YouTube es regalar la sesión.
 *
 * El banner se muestra con cualquier link válido, se pueda incrustar o no
 * (`urlExternaStreaming`, no `urlEmbedStreaming`). Si el productor cargó el
 * link de un canal, EnVivoPage ofrece el botón para abrirlo afuera: no es lo
 * ideal, pero es mucho mejor que no anunciar la transmisión. Gatearlo por
 * "incrustable" dejaba al visitante sin enterarse de que el remate se estaba
 * transmitiendo, y el editor ya empuja fuerte hacia un link incrustable
 * (error en rojo + preview del reproductor).
 *
 * A nivel de módulo, como SiteHeader/SiteFooter: definido adentro del render,
 * cada re-render (filtros, búsqueda) le cambia la identidad y React lo desmonta
 * y lo remonta.
 */
export const BannerEnVivo = ({ config, tipografia, slug, navigate }) => (
  <div
    style={{
      background: '#fff1f0',
      borderBottom: '2px solid #ffccc7',
      padding: '16px 24px',
      textAlign: 'center',
      fontFamily: tipografia.body,
      display: 'flex',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'center',
      gap: 16,
    }}
  >
    <span style={{ fontSize: 16, fontWeight: 600, color: '#a8071a', display: 'inline-flex', alignItems: 'center', gap: 10 }}>
      {/* El punto rojo pulsante es el código visual universal de "esto está
          pasando ahora". Dice en un vistazo lo que el texto tarda en explicar. */}
      <span className="puja-live-dot" aria-hidden="true" />
      Seguí nuestro remate EN VIVO a través de este link
    </span>
    <Button
      type="primary"
      size="large"
      icon={<PlayCircleOutlined />}
      onClick={() => navigate(`/remate/${slug}/en-vivo`)}
      style={{
        background: config.color_btn_primary || config.color_primary || '#1a5f2a',
        border: 'none',
        borderRadius: 32,
        fontWeight: 600,
      }}
    >
      Ver el remate
    </Button>
    <style>{`
      .puja-live-dot {
        width: 11px; height: 11px; border-radius: 50%;
        background: #f5222d; flex: none;
        animation: puja-live-pulse 1.4s ease-in-out infinite;
      }
      @keyframes puja-live-pulse {
        0%, 100% { opacity: 1;   box-shadow: 0 0 0 0    rgba(245, 34, 45, 0.55); }
        70%      { opacity: 0.65; box-shadow: 0 0 0 8px rgba(245, 34, 45, 0);    }
      }
      /* Respeta a quien pidió menos movimiento en el sistema operativo: para
         alguien con sensibilidad vestibular o migrañas, un pulso infinito en
         el tope de la página es un problema real, no una preferencia. */
      @media (prefers-reduced-motion: reduce) {
        .puja-live-dot { animation: none; }
      }
    `}</style>
  </div>
);

const RematePage = () => {
  // Le avisa al sitio incrustante cuánto medimos, para que agrande el
  // iframe en vez de dejarlo clavado en los 800px del snippet.
  useAvisarAltoAlPadre();

  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [data, setData] = useState(null);
  const [categoriaFiltro, setCategoriaFiltro] = useState(location.state?.categoriaFiltro ?? null);
  const [razaFiltro, setRazaFiltro] = useState(location.state?.razaFiltro ?? null);
  // Filtro por cabaña de procedencia. En un remate de una sola cabaña la lista
  // llega vacía y ni el filtro ni la etiqueta se dibujan.
  // Lee `location.state` igual que categoría y raza: el menú de la cabecera
  // navega con el filtro puesto cuando se lo usa desde otra pantalla.
  const [cabanaFiltro, setCabanaFiltro] = useState(location.state?.cabanaFiltro ?? null);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Deep-link cuando el micrositio está embebido en un sitio externo
  // (ej: remates.cabanapilaga.com.ar/). El script auxiliar del embedCode
  // lee ?animal=X del URL del parent y nos lo postea acá. Si recibimos,
  // navegamos al animal específico dentro del iframe.
  useEffect(() => {
    asegurarFlagLeido();
    const handler = (e) => {
      if (e.data?.type === 'navigate-animal' && e.data?.animal && !deepLinkConsumed) {
        // e.data.animal viene como 'id-slug' (ej: '42-mi-toro')
        // Solo la primera vez: después el usuario navega libre y el parent
        // (que conserva ?animal= viejo) ya no puede tirarnos de vuelta.
        deepLinkConsumed = true;
        navigate(`/remate/${slug}/animal/${e.data.animal}`);
      }
      // ?vivo=1 en el parent: el link para compartir la transmisión. Misma
      // regla de una sola vez, y por el mismo motivo — el parent conserva el
      // parámetro y sin el flag te devolvería al vivo en cada re-render.
      if (e.data?.type === 'navigate-vivo' && !deepLinkConsumed) {
        deepLinkConsumed = true;
        navigate(`/remate/${slug}/en-vivo`);
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [slug, navigate]);

  // Handshake con el parent cuando estamos embebidos: avisamos que el
  // micrositio ya montó (y su listener de 'message' está activo) para que el
  // script del embed REENVÍE el deep-link (?animal). Esto evita el race donde
  // el parent posteaba en iframe.load —antes de que React montara el listener—
  // y el mensaje se perdía, dejando la home en vez del animal.
  useEffect(() => {
    asegurarFlagLeido();
    if (!deepLinkConsumed && window.parent && window.parent !== window) {
      window.parent.postMessage({ type: 'micrositio-ready' }, '*');
    }
  }, []);

  useEffect(() => {
    const cargar = async () => {
      try {
        const res = await micrositioService.getBySlug(slug);
        setData(res.data);
        analyticsService.track('visita_index', res.data.evento.id_evento).catch(() => {});  // telemetria fire-and-forget: no se le corta la navegacion a nadie por una metrica
      } catch (err) {
        // Sin `message.error` a proposito: esto no es una accion puntual que
        // falla, es la carga entera de la pantalla. El error se comunica con el
        // <Result> a pagina completa de mas abajo, que dice mas que un toast
        // que se va en tres segundos. Un toast ADEMAS seria ruido duplicado.
        if (err.response?.status === 404) {
          setError('not_found');
        } else {
          setError('server_error');
        }
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [slug]);

  const handleVerAnimal = useCallback((animal) => {
    analyticsService.track('visita_animal', data.evento.id_evento, {
      id_animal: animal.id_animal,
      id_categoria: animal.id_categoria,
    }).catch(() => {});  // telemetria fire-and-forget: no se le corta la navegacion a nadie por una metrica
    navigate(`/remate/${slug}/animal/${buildAnimalSlug(animal)}`);
    // `navigate` fuera de deps: react-router lo mantiene estable y agregarlo
    // recrearia el callback en cada render, tirando la memoizacion que es el
    // unico motivo por el que este useCallback existe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, slug]);

  const handleCompartir = useCallback((animal) => {
    // Usa la URL custom del productor si declaró embed externo en la config;
    // sino, window.location.href (default).
    const url = getShareUrl({
      config: data.evento?.configuracion_micrositio,
      animal,
      eventoSlug: data.evento?.slug,
    });
    const nomEvento = (data.evento?.nombre_evento || '').trim();
    const corralStr = animal.corral ? ` - ${terminoLote(data.evento?.configuracion_micrositio).mayus}: ${animal.corral}` : '';
    const texto = `Mirá este lote de ${nomEvento}:\n${(animal.nom_animal || '').trim()}${corralStr}\n\n${url}`;
    // Dominio FIJO, asi que no necesita lista blanca — pero el `noopener` si:
    // no depende de a donde se va, sino de que la pestaña nueva no pueda
    // tocar `window.opener` y redirigir ESTA pagina.
    window.open(`https://wa.me/?text=${encodeURIComponent(texto)}`, '_blank', 'noopener,noreferrer');
    analyticsService.track('compartir_animal', data.evento.id_evento, {
      id_animal: animal.id_animal,
    }).catch(() => {});  // telemetria fire-and-forget: no se le corta la navegacion a nadie por una metrica
  }, [data]);

  const handleCategoriaSelect = useCallback((idCategoria) => {
    setCategoriaFiltro(idCategoria);
    if (idCategoria && data) {
      analyticsService.track('visita_categoria', data.evento.id_evento, {
        id_categoria: idCategoria,
      }).catch(() => {});  // telemetria fire-and-forget: no se le corta la navegacion a nadie por una metrica
    }
    setTimeout(() => {
      document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, [data]);

  const handleRazaSelect = useCallback((raza) => {
    setRazaFiltro(raza);
    setTimeout(() => {
      document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  const handleCabanaSelect = useCallback((cabana) => {
    setCabanaFiltro(cabana);
    setTimeout(() => {
      document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' });
    }, 50);
  }, []);

  // Colapsamos los corrales agrupados: un card por lote (no uno por animal).
  //
  // Van en useMemo porque estas dos listas bajan como props a CatalogoConFiltro,
  // que memoiza sobre ELLAS. Sueltas devolvian un array nuevo en cada render y
  // ninguno de esos useMemo se sostenia: el de `destacados` sortea al azar
  // cuando nadie tiene vistas, asi que los destacados se barajaban solos cada
  // vez que el visitante tocaba un filtro o cruzaba el umbral de scroll.
  //
  // Y van ACA ARRIBA, no donde se usan: mas abajo quedaban despues de los
  // early returns de loading/error, y un hook que no se llama siempre en el
  // mismo orden rompe React. Por eso leen `data?.animales` en vez de la
  // desestructuracion, que todavia no ocurrio.
  const animalesCatalogo = useMemo(() => colapsarAgrupados(data?.animales || []), [data]);

  const animalesFiltrados = useMemo(
    () => animalesCatalogo.filter(a => !categoriaFiltro || a.id_categoria === categoriaFiltro),
    [animalesCatalogo, categoriaFiltro],
  );

  if (loading) {
    return <PujaSpinner size="lg" label="Cargando remate…" fondo="#fff" />;
  }

  if (error === 'not_found') {
    return (
      <Result
        status="404"
        title="Remate no encontrado"
        subTitle="El enlace puede ser incorrecto o el evento ya no está disponible."
      />
    );
  }

  if (error) {
    return (
      <Result
        status="500"
        title="Error al cargar el remate"
        subTitle="Intentá de nuevo en unos momentos."
      />
    );
  }

  const { evento, categorias: categoriasRaw, sponsors } = data;
  const config = evento.configuracion_micrositio || {};
  // Orden de categorías definido por el productor para ESTE evento (vive en la
  // config del micrositio). Las no listadas van al final, en su orden original.
  const ordenCat = config.orden_categorias || [];
  // Los nombres de categoría ya vienen con el alias aplicado desde el backend
  // (micrositio_routes). Acá solo ordenamos.
  const categorias = [...categoriasRaw].sort((a, b) => {
    const ia = ordenCat.indexOf(a.id_categoria); const ib = ordenCat.indexOf(b.id_categoria);
    return (ia === -1 ? Infinity : ia) - (ib === -1 ? Infinity : ib);
  });
  // Los helpers del modulo, no la tabla cruda. El fallback copiado a mano es
  // justo la divergencia que `micrositioTema.js` existe para matar — su propio
  // docstring lo dice: "al que se olvidaba no le avisaba nada: la pagina caia
  // al default en silencio". Verificado que dan exactamente lo mismo.
  const tipografia = tipografiaDe(config);
  // El clamp compartido: `columnas_catalogo` es la MISMA clave que usa el
  // catalogo, y se habia endurecido alla nomas. El `Math.floor` tapaba el
  // sintoma —24/5 daba 4— pero dejaba una grilla de 5 columnas ocupando 20 de
  // 24, con una franja muerta a la derecha.
  const colCatalogo = columnasValidas(config);
  const spanPorColumna = 24 / colCatalogo;
  const tipoCabecera = config.cabecera || 'cabecera-1';
  const headerHeight = altoHeaderDe(config);

  // El switch crudo y NO `estado_ofertas.abiertas`, a diferencia del candado:
  // esto solo decide si la tarjeta del catálogo muestra el precio base, y
  // mostrarlo con la ventana todavía programada es lo que se quiere — el
  // comprador necesita saber el precio ANTES de que abra para decidir si
  // participa. Quién puede ofertar lo sigue decidiendo `estado_ofertas`
  // (AnimalPage y el backend), que es una sola fuente de verdad.
  const mostrarPrecioBase = evento.ofertas_activas === 1;

  const cssVars = {
    '--color-primary': config.color_primary || '#1a5f2a',
    '--color-secondary': config.color_secondary || '#8B4513',
    '--color-title': config.color_title || '#1a1a1a',
    '--color-paragraph': config.color_paragraph || '#666666',
    '--color-btn-primary': config.color_btn_primary || '#1a5f2a',
  };

  const razas = [...new Set(animalesCatalogo.filter(a => a.nom_raza).map(a => a.nom_raza))].sort();
  // `nom_cabana_filtro`, y NO `nom_cabana` (la etiqueta): ese segundo omite
  // al organizador a propósito para no repetir su marca en cada fila, pero el
  // filtro necesita justo lo contrario — que el organizador sea una opción
  // más, igual que cualquier invitada. Ver `Animal.resolver_procedencia()`.
  // Mismo criterio que las razas: se derivan del catálogo y no de la tabla,
  // así una cabaña sin ningún animal vinculado no aparece en el filtro.
  const cabanas = [...new Set(animalesCatalogo.filter(a => a.nom_cabana_filtro).map(a => a.nom_cabana_filtro))].sort();

  // Hero desactivado para el catálogo → sin banner, header sólido arriba.
  const heroOff = !!config.hero_off_catalogo;
  const colorHeader = config.color_header || config.color_primary || '#1a5f2a';
  const colorHeaderInitial = heroOff ? colorHeader : (config.color_header_initial || 'transparent');
  const isTwoRow = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  const heroTransparente = !heroOff && !isTwoRow && colorHeaderInitial === 'transparent';
  // Transparente: hero detrás del header (marginTop=0, paddingTop para el título)
  // Sólido / opciones 3-4: hero debajo del header (marginTop, sin paddingTop extra)
  const heroMarginTop = heroMargenSuperior(config, headerHeight, heroTransparente);
  const heroPaddingTop = heroTransparente ? headerHeight : 0;

  // El tope de mobile (60px: MobileBar es 1 fila de ~56-64px) vive en la regla
  // `.micrositio-page .hero-zone` de index.css, junto con el de
  // `.hero-off-spacer`. Acá solo se calculan los valores de desktop.

  // CSS vars del hero — el inline style usa estas vars y las media queries
  // las redefinen para mobile. Asi evitamos el conflicto "inline gana sobre
  // !important" en algunos browsers (Safari iOS especialmente).
  const heroVars = {
    '--hero-margin-top':  `${heroMarginTop}px`,
    '--hero-padding-top': `${heroPaddingTop}px`,
  };

  return (
    <div
      className="micrositio-page"
      style={{ fontFamily: tipografia.body, minHeight: '100vh', ...cssVars, ...heroVars }}
    >
      {/* Estilos del boton WhatsApp share (hover slide) */}
      <style>{WA_SHARE_BTN_STYLES}</style>

      {/* Responsive: en mobile el header colapsa a una fila ~60px.
          Anulamos las CSS vars del hero, que el inline style consume. */}
      {/* ── CABECERA sticky ── */}
      <SiteHeader
        config={config}
        tipografia={tipografia}
        logoUrl={evento.img_evento}
        evento={evento}
        categorias={categorias}
        razas={razas}
        cabanas={cabanas}
        scrolled={scrolled}
        onCategoriaSelect={handleCategoriaSelect}
        onRazaSelect={handleRazaSelect}
        onCabanaSelect={handleCabanaSelect}
        isAuthenticated={isAuthenticated}
        slug={slug}
      />

      {/* Cuenta regresiva de la ventana de pre-ofertas, colgada del header.
          Descuenta contra la hora del SERVIDOR (estado_ofertas.ahora), no
          contra el reloj del visitante. */}
      <CountdownPreOfertas
        estadoOfertas={evento.estado_ofertas}
        top={headerHeight}
        colorPrimary={config.color_primary}
        antesDeRecargar={marcarDeepLinkConsumido}
      />

      {/* ── HERO (oculto si hero_off_catalogo) ── */}
      {heroOff ? (
        // Spacer para compensar el header fixed (que ahora es sólido). El
        // countdown NO entra en esta cuenta: es una pastilla angosta que flota
        // a la derecha y no empuja contenido.
        <div className="hero-off-spacer" style={{ '--hero-off-header': `${headerHeight}px` }} />
      ) : (
        <>
          {/* marginTop / paddingTop vienen de las CSS vars --hero-* (definidas
              arriba) para que la media query mobile pueda overrideralas
              (los inline styles ganarian sobre !important en Safari iOS). */}
          <div
            className="hero-zone"
            style={{
              background: heroBackground(config, evento.imgsec_evento ? fixUrl(evento.imgsec_evento) : null),
              minHeight: config.hero_altura || (heroTransparente ? 300 : 210),
              display: 'flex',
              justifyContent: 'center',
              ...heroTextLayout(config),
            }}
          >
            <Title
              level={1}
              style={{
                color: config.color_hero_text || '#ffffff',
                fontFamily: tipografia.title,
                fontSize: config.hero_text_size || 32,
                margin: 0,
                marginTop: -10,
                textAlign: 'center',
                paddingLeft: 24,
                paddingRight: 24,
                textTransform: config.text_transform_hero || 'none',
                ...heroTextPadding(config),
              }}
            >
              {evento.nombre_evento}
            </Title>
          </div>

          {/* ── DIVIDER COLOR PRINCIPAL ── */}
          <div style={{ height: 5, background: config.color_primary || '#1a5f2a' }} />
        </>
      )}

      {/* ── BANNER TRANSMISIÓN EN VIVO ──
          Solo mientras el remate está en curso: si el evento ya terminó y el
          productor se olvidó de borrar el link, anunciar "en vivo" es mentirle
          al visitante. El banner de finalizado, abajo, es el que corresponde. */}
      {!evento.es_finalizado && urlExternaStreaming(config.url_streaming) && (
        <BannerEnVivo config={config} tipografia={tipografia} slug={slug} navigate={navigate} />
      )}

      {/* ── BANNER EVENTO FINALIZADO ── */}
      {evento.es_finalizado && (
        <div style={{
          background: '#fff8e1',
          borderBottom: '2px solid #ffe082',
          padding: '18px 24px',
          textAlign: 'center',
          fontFamily: tipografia.body,
        }}>
          <span style={{ fontSize: 16, fontWeight: 600, color: '#b45309' }}>
            🏁 Evento finalizado — gracias por participar de nuestro remate
          </span>
        </div>
      )}

      {/* ── CATÁLOGO ── */}
      <div id="catalogo" style={{ maxWidth: 1400, margin: '0 auto', padding: '70px 24px' }}>
        <CatalogoConFiltro
          animales={animalesCatalogo}
          animalesFiltrados={animalesFiltrados}
          categorias={categorias}
          categoriaFiltro={categoriaFiltro}
          setCategoriaFiltro={setCategoriaFiltro}
          razaFiltro={razaFiltro}
          cabanaFiltro={cabanaFiltro}
          setCabanaFiltro={setCabanaFiltro}
          cabanas={cabanas}
          setRazaFiltro={setRazaFiltro}
          razas={razas}
          config={config}
          tipografia={tipografia}
          evento={evento}
          spanPorColumna={spanPorColumna}
          ofertasActivas={mostrarPrecioBase}
          onVer={handleVerAnimal}
          onCompartir={handleCompartir}
          sponsors={sponsors}
        />
      </div>

      {/* ── FOOTER ── */}
      <SiteFooter
        config={config}
        tipografia={tipografia}
        evento={evento}
        categorias={categorias}
        logoUrl={evento.img_evento}
        onCategoriaSelect={handleCategoriaSelect}
        slug={slug}
      />

      {/* El modal de detalle vivia aca, pero era inalcanzable: nada seteaba
          `animalSeleccionado` a un animal (handleVerAnimal navega a AnimalPage),
          asi que la condicion nunca daba true y el import arrastraba el
          componente al bundle para nada. */}
    </div>
  );
};


// ── Header del micrositio ──────────────────────────────────────────────
/**
 * @param {Array} [navPropia]  Items de navegacion que REEMPLAZAN a los del
 *   remate. Cada uno: `{ key, label, onClick }` o `{ key, label, items }` para
 *   un desplegable.
 *
 *   Existe porque este header se reusa en el catalogo de genetica, y ahi los
 *   items de siempre —Informacion, Condiciones, Contacto— apuntan a
 *   `/remate/:slug/...`, rutas que en el catalogo NO EXISTEN: el comprador
 *   tocaba "Contacto" y caia en un 404. Un item de menu que no lleva a ningun
 *   lado es peor que no tenerlo.
 *
 *   Sin esta prop el header dibuja lo de siempre, asi que el micrositio de
 *   remate —que esta en produccion— no cambia en nada.
 *
 * @param {React.ReactNode} [franjaSuperior]  Nodo que se dibuja como PRIMER
 *   hijo de `<header className="site-header">`, encima de todo lo demas
 *   —desktop y mobile—. Existe para la franja de bienvenida de OP-1 del
 *   catalogo de genetica (`BarraBienvenida`, variante `"franja"`): tiene que
 *   quedar DENTRO de la cabecera fija para que `.site-header` la mida junto
 *   con el resto, y afuera —en el flujo normal, antes de este componente—
 *   quedaba tapada por el propio header (`position: fixed; zIndex: 1000`) y
 *   nunca se llegaba a ver. `null` por defecto: el micrositio de remate no
 *   manda esta prop y el header no cambia en nada.
 */
// Aire entre el borde de la pantalla y lo primero/último de la cabecera, en
// escritorio Y en mobile. Una sola constante: la barra mobile tenía 16 por su
// cuenta y el burger quedaba más cerca del borde que el botón MI PANEL en
// escritorio (pedido del usuario, 2026-10-15).
const HEADER_PADDING_LATERAL = 24;

export const SiteHeader = ({
  config, tipografia, logoUrl, evento = null,
  categorias = [], razas = [], cabanas = [], scrolled,
  onCategoriaSelect, onRazaSelect, onCabanaSelect,
  isAuthenticated, slug, navPropia = null, rutaInicio = null,
  franjaSuperior = null,
}) => {
  // El rol sale del context, no de props: los cuatro llamadores ya pasan
  // `isAuthenticated` pero ninguno el usuario, y sin el rol este boton mandaba
  // a TODOS a /dashboard, que es el panel del productor.
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  // LA FRANJA SE ESCONDE AL SCROLLEAR, y se esconde DESLIZANDO LA CABECERA
  // ENTERA hacia arriba exactamente su alto, con `transform`. No se colapsa la
  // franja: las tres páginas del catálogo miden `.site-header` para dejarle
  // lugar al contenido de arriba, y un header que se achica al scrollear
  // achicaría esa medida y movería TODA la página 30px en el medio del scroll.
  // `transform` no toca el layout —la medida queda igual— y visualmente la
  // franja sale por arriba y el menú queda pegado al borde. El alto se mide
  // del DOM y se sigue midiendo (el mensaje puede pasar a dos líneas en un
  // teléfono): un número fijo acá volvería a desalinear el día que cambie.
  const franjaRef = useRef(null);
  const [altoFranja, setAltoFranja] = useState(0);
  useEffect(() => {
    const nodo = franjaRef.current;
    if (!nodo) { setAltoFranja(0); return undefined; }
    const medir = () => setAltoFranja(nodo.offsetHeight);
    medir();
    const observador = new ResizeObserver(medir);
    observador.observe(nodo);
    return () => observador.disconnect();
    // `Boolean(...)` y no el nodo: el padre reconstruye ese elemento en cada
    // render, y con él en las deps el observador se desarmaba y rearmaba
    // cada vez. Lo único que importa es que la franja aparezca o desaparezca;
    // los cambios de alto los sigue el ResizeObserver solo.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Boolean(franjaSuperior)]);

  // A DONDE VUELVE EL LOGO. Es el afordance de "ir al inicio" mas clickeado de
  // cualquier sitio, y estaba clavado a `/remate/:slug` en las tres variantes
  // del logo (desktop con imagen, desktop sin imagen, mobile). En el catalogo
  // ese slug es el del CATALOGO: `RematePage` montaba, `getBySlug` devolvia 404
  // y el comprador terminaba en "Remate no encontrado" — desde el logo, en la
  // vidriera. Se arreglo el menu, el drawer y el pie, y el logo no estaba en
  // ninguna de esas listas.
  const irAlInicio = () => navigate(rutaInicio || `/remate/${slug}`);

  // Link activo según la ruta actual: subrayado color secundario solo en la
  // página donde estamos parados (Inicio / Información / Contacto).
  const colorSecondary = config.color_secondary || '#8B4513';
  // Catálogo PDF del evento (si el productor lo cargó) → abre en nueva pestaña.
  const catalogoPdf = config.catalogo_pdf_url ? fixUrl(config.catalogo_pdf_url) : null;
  const _path = location.pathname;
  const activeKey =
    /\/(condiciones|reclamos)/.test(_path) ? 'info'
    : _path.endsWith('/contacto')          ? 'contacto'
    : _path === `/remate/${slug}`          ? 'inicio'
    : null;

  // Todo derivado de config — no se necesita pasar como prop
  const colorPrimary      = config.color_primary || '#1a5f2a';
  const colorHeader       = config.color_header || colorPrimary;
  const colorHeaderInitial = config.color_header_initial || 'transparent';
  const colorNav          = config.color_nav || '#ffffff';
  const tipoCabecera      = config.cabecera || 'cabecera-1';
  const isTwoRow          = tipoCabecera === 'cabecera-2' || tipoCabecera === 'cabecera-3';
  const headerBg          = isTwoRow ? colorHeader : (scrolled ? colorHeader : colorHeaderInitial);

  const headerStyle = {
    position: 'fixed',
    top: 0, left: 0, right: 0,
    zIndex: 1000,
    background: headerBg,
    boxShadow: (scrolled || isTwoRow) ? '0 2px 8px rgba(0,0,0,0.18)' : 'none',
    // Ver `altoFranja`: scrolleado, la cabecera sube el alto de la franja y
    // ésta queda fuera de la pantalla. Sin franja el transform es `none`.
    transform: scrolled && altoFranja ? `translateY(-${altoFranja}px)` : 'none',
    transition: 'background 0.3s, box-shadow 0.3s, transform 0.3s',
  };

  // `site-header-inner` no es decorativo: el countdown mide este contenedor para
  // alinearse con el borde derecho del contenido, igual que el botón MI PANEL y
  // el menú hamburguesa. Con un `right` fijo quedaba desalineado en cuanto la
  // pantalla no medía lo que se había supuesto.
  const inner = (children) => (
    <div className="site-header-inner" style={{ maxWidth: 1400, margin: '0 auto', padding: `0 ${HEADER_PADDING_LATERAL}px` }}>
      {children}
    </div>
  );

  const navLinkStyle = {
    color: colorNav,
    fontSize: 14,
    fontWeight: 500,
    padding: '4px 12px',
    cursor: 'pointer',
    fontFamily: tipografia.body,
    whiteSpace: 'nowrap',
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    transition: 'opacity 0.2s',
    textTransform: 'uppercase',
  };

  // Estilo del link con subrayado (color secundario) si es la página actual.
  const navLink = (key) => activeKey === key
    ? { ...navLinkStyle, borderBottom: `3px solid ${colorSecondary}`, paddingBottom: 2 }
    : navLinkStyle;

  const btnAccederStyle = {
    background: config.color_btn_primary || colorPrimary,
    border: 'none',
    marginLeft: 8,
    fontFamily: tipografia.body,
    padding: '10px 18px',
    borderRadius: 32,
    height: 'auto',
    lineHeight: 1,
    textTransform: 'uppercase',
  };

  const logoEl = logoUrl ? (
    <img
      src={fixUrl(logoUrl)}
      alt="Logo"
      style={{ maxHeight: config.logo_altura || 44, objectFit: 'contain', display: 'block', cursor: 'pointer' }}
      onClick={irAlInicio}
    />
  ) : (
    <span
      style={{ color: colorNav, fontFamily: tipografia.title, fontWeight: 700, fontSize: 17, cursor: 'pointer' }}
      onClick={irAlInicio}
    >
      {evento?.nombre_evento || ''}
    </span>
  );

  // Logo MOBILE, opcional (2026-10-14; hasta entonces este lugar lo ocupaba el
  // iso). Un logo tipo wordmark (icono + texto) es ancho y en la barra mobile
  // empuja el burger fuera de pantalla; si el productor cargó uno compacto lo
  // usamos ahí. Si no, cae al principal (el wrapper en MobileBar ya lo deja
  // shrinkear en vez de desbordar). Su alto, sin elegir, es el del principal.
  const logoMobileUrl = config.logo_mobile_url ? fixUrl(config.logo_mobile_url) : null;
  const logoMobileEl = logoMobileUrl ? (
    <img
      src={logoMobileUrl}
      alt="Logo"
      style={{ maxHeight: config.logo_mobile_altura || config.logo_altura || 44, maxWidth: '100%', objectFit: 'contain', display: 'block', cursor: 'pointer' }}
      onClick={irAlInicio}
    />
  ) : logoEl;

  // El iso es el favicon del micrositio.
  useFaviconMicrositio(config.logo_iso_url ? fixUrl(config.logo_iso_url) : null);

  const categoriasMenuItems = categorias.map(c => ({
    key: `cat-${c.id_categoria}`,
    label: c.nom_categoria,
    onClick: () => onCategoriaSelect
      ? onCategoriaSelect(c.id_categoria)
      : navigate(`/remate/${slug}`, { state: { categoriaFiltro: c.id_categoria } }),
  }));

  const razasMenuItems = razas.map(r => ({
    key: `raza-${r}`,
    label: r,
    onClick: () => onRazaSelect
      ? onRazaSelect(r)
      : navigate(`/remate/${slug}`, { state: { razaFiltro: r } }),
  }));

  // Vacío en un remate de una sola cabaña: ahí el menú no se muestra.
  const cabanasMenuItems = cabanas.map(c => ({
    key: `cab-${c}`,
    label: c,
    onClick: () => onCabanaSelect
      ? onCabanaSelect(c)
      : navigate(`/remate/${slug}`, { state: { cabanaFiltro: c } }),
  }));

  const infoMenuItems = [
    { key: 'cond-comercial', label: 'Condiciones comerciales', onClick: () => navigate(`/remate/${slug}/condiciones-comerciales`) },
    { key: 'cond-sanitaria', label: 'Condiciones sanitarias', onClick: () => navigate(`/remate/${slug}/condiciones-sanitarias`) },
    { key: 'reclamos', label: 'Reclamos y garantías', onClick: () => navigate(`/remate/${slug}/reclamos-garantias`) },
  ];

  // ── Fragmentos del header ───────────────────────────────────────────
  // OJO: son funciones que se INVOCAN ({accederBtn()}), no componentes que se
  // montan (<AccederBtn />). Definir un componente dentro del render de otro
  // crea una funcion nueva en cada render, React la ve como otro tipo y
  // desmonta/remonta el subarbol. Aca duele especialmente: SiteHeader
  // re-renderiza con cada cambio de `scrolled` (listener de scroll), asi que
  // el <img> del logo dentro de mobileBar se volvia a pedir al navegador en
  // cada scroll — de ahi el parpadeo. Invocadas, el JSX se integra al arbol
  // del padre y no hay remonte. Mismo patron que `inner()` mas arriba.

  // El productor puede ocultar el botón Acceder de la CABECERA (config). Default mostrar.
  const mostrarAcceso = config.mostrar_btn_acceso !== false;
  const accederBtn = (block = false) => {
    if (!mostrarAcceso) return null;
    return (
      <Button
        type="primary"
        icon={isAuthenticated ? <AppstoreOutlined /> : <LoginOutlined />}
        block={block}
        style={block
          ? { ...btnAccederStyle, marginLeft: 0, width: '100%', textAlign: 'center' }
          : btnAccederStyle
        }
        onClick={() => { navigate(rutaAcceso(isAuthenticated, user, slug)); setMenuOpen(false); }}
      >
        {isAuthenticated ? 'Mi Panel' : 'Acceder'}
      </Button>
    );
  };

  // ── Desktop nav ────────────────────────────────────────────────────
  // Los desplegables viven ACA y no repetidos dentro de cada layout. layout2
  // (cabecera-2) tenia su propia copia del nav y se habia quedado sin Razas: el
  // menu perdia una entrada segun la cabecera que eligiera el productor, y como
  // cabecera-3 cae en layout2 al scrollear, ahi Razas aparecia y desaparecia.
  const dropdownNav = (label, items, estilo = navLinkStyle) => (
    <Dropdown menu={{ items }} trigger={['hover']}>
      <span style={estilo}>{label} <DownOutlined style={{ fontSize: 10 }} /></span>
    </Dropdown>
  );

  // Filtros derivados del catálogo: cada uno se dibuja solo si tiene algo.
  const dropsFiltros = () => (
    <>
      {categorias.length > 0 && dropdownNav('Categorías', categoriasMenuItems)}
      {razas.length > 0 && dropdownNav('Razas', razasMenuItems)}
      {cabanas.length > 1 && dropdownNav('Cabañas', cabanasMenuItems)}
    </>
  );

  const menuItems = () => {
    // Navegacion inyectada: la usa el catalogo de genetica. Se dibuja SOLO lo
    // que le pasan — sin Inicio ni Contacto del remate, que alla no existen.
    if (navPropia) {
      return (
        <>
          {navPropia.map((item) => (
            item.items
              ? <React.Fragment key={item.key}>
                  {dropdownNav(item.label, item.items, navLinkStyle)}
                </React.Fragment>
              : <span key={item.key} style={navLinkStyle} onClick={item.onClick}>
                  {item.label}
                </span>
          ))}
        </>
      );
    }
    return (
      <>
        <span style={navLink('inicio')} onClick={() => navigate(`/remate/${slug}`)}>Inicio</span>
        {dropsFiltros()}
        {dropdownNav('Información', infoMenuItems, navLink('info'))}
        {catalogoPdf && <span style={navLinkStyle} onClick={() => abrirExterna(catalogoPdf)}>Catálogo PDF</span>}
        <span style={navLink('contacto')} onClick={() => navigate(`/remate/${slug}/contacto`)}>Contacto</span>
      </>
    );
  };

  // ── Mobile burger button ────────────────────────────────────────────
  const burgerBtn = () => (
    <button
      className="site-header-burger"
      onClick={() => setMenuOpen(true)}
      style={{
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 8,
        color: colorNav,
        width: 38, height: 38,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        cursor: 'pointer',
        flexShrink: 0,
      }}
    >
      <MenuOutlined style={{ fontSize: 16 }} />
    </button>
  );

  // ── Mobile bar (logo + acceder + burger) ──────────────────────────
  // El logo puede shrinkear (flexShrink 1 + overflow hidden) para que, aun
  // sin logo mobile cargado, nunca empuje el botón de menú fuera de pantalla.
  // El `gap` de 16 es lo que separa el logo del burger cuando el logo es un
  // wordmark ancho que llena la barra: con 8 quedaban pegados.
  //
  // `flex: 1` NO es decorativo. Este div va adentro de OTRO `.site-header-mobile`
  // (el wrapper de abajo), y el CSS del header le pone `display: flex` a los
  // dos. Como hijo de un flex, sin `flex: 1` esta barra mide lo que mide su
  // contenido y no el ancho de la pantalla: a 375px el logo la llenaba y no
  // se notaba, pero a 430px (celulares grandes) sobraban ~55px a la derecha y
  // el `marginLeft: auto` del burger no tenia adonde empujar (2026-10-15).
  const mobileBar = () => (
    <div className="site-header-mobile" style={{ display: 'flex', alignItems: 'center', flex: 1, minWidth: 0, padding: `12px ${HEADER_PADDING_LATERAL}px`, gap: 16 }}>
      <div style={{ flexShrink: 1, minWidth: 0, overflow: 'hidden' }}>{logoMobileEl}</div>
      <div style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
        {accederBtn()}
        {burgerBtn()}
      </div>
    </div>
  );

  // ── Drawer content ────────────────────────────────────────────────
  const drawerLinkStyle = {
    display: 'block', padding: '11px 0', fontSize: 15,
    color: '#222', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
    fontFamily: tipografia.body,
  };
  const drawerGroupTitle = {
    fontSize: 11, fontWeight: 700, color: '#999',
    textTransform: 'uppercase', letterSpacing: 1,
    padding: '14px 0 6px', fontFamily: tipografia.body,
  };
  const drawerSubLinkStyle = {
    display: 'block', padding: '8px 0 8px 12px', fontSize: 14,
    color: '#444', cursor: 'pointer', borderBottom: '1px solid #f8f8f8',
    fontFamily: tipografia.body,
  };

  const closeDrawer = () => setMenuOpen(false);

  const drawerContent = () => (
    <div>
      {/* CON NAVEGACION PROPIA NO SE DIBUJA NADA DEL REMATE, y esto valia para
          todo el drawer y no para un solo item. La regla ya estaba escrita mas
          abajo —"un Contacto que en el catalogo da 404 no mejora por estar en
          mobile"— pero aplicada SOLO a Contacto: `Inicio` y las tres de
          `Informacion` seguian dibujandose siempre, apuntando a
          `/remate/:slug/...`, y el catalogo pasa el slug del CATALOGO. En un
          telefono eran cuatro links a 404, en la pantalla donde el cabañero
          mira el catalogo. El desktop ya lo hacia bien (`menuItems`) y el
          `SiteFooter` tambien; el drawer se habia quedado atras. */}
      {!navPropia && (
        <div style={drawerLinkStyle} onClick={() => { navigate(`/remate/${slug}`); closeDrawer(); }}>Inicio</div>
      )}

      {categorias.length > 0 && <>
        <div style={drawerGroupTitle}>Categorías</div>
        {categorias.map(c => (
          <div key={c.id_categoria} style={drawerSubLinkStyle} onClick={() => {
            if (onCategoriaSelect) onCategoriaSelect(c.id_categoria);
            else navigate(`/remate/${slug}`, { state: { categoriaFiltro: c.id_categoria } });
            closeDrawer();
          }}>{c.nom_categoria}</div>
        ))}
      </>}

      {razas.length > 0 && <>
        <div style={drawerGroupTitle}>Razas</div>
        {razas.map(r => (
          <div key={r} style={drawerSubLinkStyle} onClick={() => {
            if (onRazaSelect) onRazaSelect(r);
            else navigate(`/remate/${slug}`, { state: { razaFiltro: r } });
            closeDrawer();
          }}>{r}</div>
        ))}
      </>}

      {cabanas.length > 1 && <>
        <div style={drawerGroupTitle}>Cabañas</div>
        {cabanas.map(c => (
          <div key={c} style={drawerSubLinkStyle} onClick={() => {
            if (onCabanaSelect) onCabanaSelect(c);
            else navigate(`/remate/${slug}`, { state: { cabanaFiltro: c } });
            closeDrawer();
          }}>{c}</div>
        ))}
      </>}

      {/* `infoMenuItems` esta hardcodeado a /remate/:slug/condiciones-comerciales,
          -sanitarias y reclamos-garantias: rutas que en un catalogo no existen. */}
      {!navPropia && <>
        <div style={drawerGroupTitle}>Información</div>
        {infoMenuItems.map(item => (
          <div key={item.key} style={drawerSubLinkStyle} onClick={() => { item.onClick(); closeDrawer(); }}>
            {item.label}
          </div>
        ))}
      </>}

      {/* Con navegacion propia, el drawer dibuja ESA y no la del remate. Un
          "Contacto" que en el catalogo da 404 no mejora por estar en mobile. */}
      {/* LOS ITEMS CON SUBMENU SE APLANAN, no se descartan. Antes habia un
          `filter((i) => !i.items)` que los tiraba, y en el catalogo esos items
          son justamente las SECCIONES —toros, hembras y sus animales—: en
          mobile la portada quedaba sin navegacion de secciones, y una cabaña
          que no configuro links propios se quedaba con el menu VACIO.

          Un drawer no tiene hover, asi que no puede haber desplegable: se
          dibuja grupo + sublinks, con la misma forma que Categorias y Razas
          mas arriba. El titulo de seccion no navega —tampoco navega en
          desktop, es solo el rotulo del grupo— y los hijos van a la ficha de
          cada animal. */}
      {navPropia
        ? navPropia.map((item) => (
            item.items && item.items.length > 0
              ? (
                <React.Fragment key={item.key}>
                  <div style={drawerGroupTitle}>{item.label}</div>
                  {item.items.map((sub) => (
                    <div
                      key={sub.key}
                      style={drawerSubLinkStyle}
                      onClick={() => { sub.onClick?.(); closeDrawer(); }}
                    >
                      {sub.label}
                    </div>
                  ))}
                </React.Fragment>
              )
              : (
                <div
                  key={item.key}
                  style={drawerLinkStyle}
                  onClick={() => { item.onClick?.(); closeDrawer(); }}
                >
                  {item.label}
                </div>
              )
          ))
        : <div style={drawerLinkStyle} onClick={() => { navigate(`/remate/${slug}/contacto`); closeDrawer(); }}>Contacto</div>}

      <Divider />
      {accederBtn(true)}
    </div>
  );

  // ── Desktop layouts por variante ──────────────────────────────────

  const layout1 = () => inner(
    <div style={{ position: 'relative', display: 'flex', alignItems: 'center', padding: '14px 0' }}>
      <div style={{ flexShrink: 0 }}>{logoEl}</div>
      <div style={{ position: 'absolute', left: '50%', transform: 'translateX(-50%)', display: 'flex', alignItems: 'center' }}>
        {menuItems()}
      </div>
      <div style={{ marginLeft: 'auto' }}>{accederBtn()}</div>
    </div>
  );

  const layout2 = () => inner(
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
      {/* CON NAVEGACION PROPIA, `menuItems()` Y NADA MAS.
          Este layout tenia su PROPIA copia del nav —Inicio, Información y
          Contacto escritos a mano— que salteaba `menuItems()` por completo y
          por lo tanto ignoraba `navPropia`. `layout1` usa `menuItems()` y
          estaba bien; este no, y lo usan cabecera-2 SIEMPRE y cabecera-3 al
          scrollear. Pilagá esta en cabecera-1, asi que no se veia: era un 404
          esperando a la primera cabaña que eligiera otra cabecera.
          Es el mismo error que ya se habia pagado en este archivo con los
          desplegables, cuando layout2 se quedo sin Razas por tener su copia. */}
      {navPropia ? (
        <>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            {menuItems()}
          </div>
          <div style={{ flexShrink: 0, padding: '0 24px' }}>{logoEl}</div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {accederBtn()}
          </div>
        </>
      ) : (
        <>
          {/* El logo parte el nav en dos: a la izquierda lo que navega el
              catálogo (Inicio + los filtros), a la derecha lo institucional y
              el acceso. */}
          <div style={{ flex: 1, display: 'flex', alignItems: 'center' }}>
            <span style={navLinkStyle} onClick={irAlInicio}>Inicio</span>
            {dropsFiltros()}
          </div>
          <div style={{ flexShrink: 0, padding: '0 24px' }}>{logoEl}</div>
          <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
            {dropdownNav('Información', infoMenuItems)}
            {catalogoPdf && <span style={navLinkStyle} onClick={() => abrirExterna(catalogoPdf)}>Catálogo PDF</span>}
            <span style={navLinkStyle} onClick={() => navigate(`/remate/${slug}/contacto`)}>Contacto</span>
            {accederBtn()}
          </div>
        </>
      )}
    </div>
  );

  const desktopContent = () => {
    if (tipoCabecera === 'cabecera-1') return layout1();
    if (tipoCabecera === 'cabecera-2') return layout2();
    if (tipoCabecera === 'cabecera-3') {
      if (scrolled) return layout2();
      return (
        <>
          {inner(<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '14px 0' }}>{logoEl}</div>)}
          <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
            {inner(<div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '10px 0' }}>
              {menuItems()}{accederBtn()}
            </div>)}
          </div>
        </>
      );
    }
    // cabecera-4
    if (scrolled) return layout1();
    return (
      <>
        {inner(<div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '14px 0' }}>
          {logoEl}{accederBtn()}
        </div>)}
        <div style={{ borderTop: '1px solid rgba(255,255,255,0.15)' }}>
          {inner(<div style={{ display: 'flex', alignItems: 'center', padding: '10px 0' }}>{menuItems()}</div>)}
        </div>
      </>
    );
  };

  return (
    <>
      <style>{`
        .site-header-mobile { display: none !important; }
        .site-header-desktop { display: block; }
        @media (max-width: 991px) {
          .site-header-mobile { display: flex !important; }
          .site-header-desktop { display: none !important; }
        }
      `}</style>

      {/* `site-header` no es decorativo: el countdown se ancla al borde inferior
          REAL de este elemento midiéndolo del DOM. Si se saca la clase, la
          pastilla vuelve a adivinar su posición con constantes. */}
      <header className="site-header" style={headerStyle}>
        {franjaSuperior && <div ref={franjaRef}>{franjaSuperior}</div>}
        <div className="site-header-mobile">{mobileBar()}</div>
        <div className="site-header-desktop">{desktopContent()}</div>
      </header>

      <Drawer
        open={menuOpen}
        onClose={closeDrawer}
        placement="right"
        width={300}
        closeIcon={<CloseOutlined />}
        title={
          <div style={{ fontFamily: tipografia.title, fontWeight: 700, fontSize: 15, color: colorPrimary }}>
            {evento?.nombre_evento || 'Menú'}
          </div>
        }
        styles={{ body: { padding: '8px 20px 24px' } }}
      >
        {drawerContent()}
      </Drawer>
    </>
  );
};


// ── Tarjeta de animal ──────────────────────────────────────────────
// Estilos del boton WhatsApp share: idle solo icono, hover expande + texto.
// Insertado UNA vez (las styled-jsx no soportan dedupe sin lib, asi que
// dejamos el style en el render del catalogo - es chico y se cachea).
const WA_SHARE_BTN_STYLES = `
  .puja-wa-share-btn {
    display: inline-flex;
    align-items: center;
    gap: 0;
    background: #25D366;
    color: #fff;
    border: none;
    border-radius: 32px;
    height: 38px;
    padding: 0 11px;
    font-size: 14px;
    line-height: 1;
    cursor: pointer;
    overflow: hidden;
    transition: background 0.25s ease, padding 0.35s ease, gap 0.35s ease;
    white-space: nowrap;
  }
  .puja-wa-share-btn:hover, .puja-wa-share-btn:focus-visible {
    background: #43e07a;
    padding: 0 18px 0 14px;
    gap: 8px;
    outline: none;
  }
  .puja-wa-share-btn__label {
    max-width: 0;
    opacity: 0;
    transition: max-width 0.35s ease, opacity 0.3s ease 0.08s;
  }
  .puja-wa-share-btn:hover .puja-wa-share-btn__label,
  .puja-wa-share-btn:focus-visible .puja-wa-share-btn__label {
    max-width: 120px;
    opacity: 1;
  }
  .puja-wa-share-btn .anticon {
    font-size: 16px;
    flex-shrink: 0;
  }
`;

// Miniatura de animal con lazy-load + cascada de imagen (foto → thumb YouTube
// → fallback del evento) + placeholder si todo falla. Usada en Destacados.
const DestacadoImg = ({ animal, config = {} }) => {
  const [error, setError] = useState(false);
  const url = imagenAnimalUrl(animal, config);
  if (!url || error) {
    return (
      <div style={{ width: '100%', height: 90, background: '#e8e8e8', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#bbb', fontSize: 11 }}>
        Sin imagen
      </div>
    );
  }
  return (
    <img
      src={url}
      alt={animal.nom_animal}
      loading="lazy"
      onError={() => setError(true)}
      style={{ width: '100%', height: 90, objectFit: 'cover', display: 'block' }}
    />
  );
};


const AnimalCard = ({ animal, config, tipografia, ofertasActivas, onVer, onCompartir, multiCabana = false }) => {
  const [hovered, setHovered] = useState(false);
  const [imgError, setImgError] = useState(false);
  const [premHover, setPremHover] = useState(false);
  const colorPrimary = config.color_primary || '#1a5f2a';
  // Cascada: foto principal → thumb del video → imagen fallback del evento.
  const imagenUrl = imagenAnimalUrl(animal, config);
  // El thumb de YouTube se amplía a 134% (centrado) para recortar sus bordes.
  const esYtThumb = !!imagenUrl && imagenUrl === youtubeThumb(animal.codigo_video);

  return (
    <Card
      hoverable
      styles={{ body: { padding: 12 } }}
      cover={
        <div
          style={{ position: 'relative', overflow: 'hidden', height: 200, background: '#f0f0f0', cursor: 'pointer' }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
          onClick={() => onVer(animal)}
        >
          {animal.es_premium === 1 && (
            <div
              onMouseEnter={() => setPremHover(true)}
              onMouseLeave={() => setPremHover(false)}
              style={{
                position: 'absolute', top: 8, left: 8, zIndex: 3,
                display: 'inline-flex', alignItems: 'center', gap: premHover ? 6 : 0,
                background: colorPrimary, color: '#fff',
                borderRadius: 20, padding: premHover ? '4px 12px' : '6px',
                fontWeight: 600, fontSize: 12, lineHeight: 1,
                boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                transition: 'gap 0.2s ease, padding 0.2s ease',
                overflow: 'hidden', whiteSpace: 'nowrap',
              }}
            >
              {premHover && <span>Premium</span>}
              <StarFilled style={{ fontSize: 14 }} />
            </div>
          )}
          {imagenUrl && !imgError ? (
            <img
              src={imagenUrl}
              alt={animal.nom_animal}
              loading="lazy"
              onError={() => setImgError(true)}
              style={{
                width: esYtThumb ? '134%' : '100%',
                marginLeft: esYtThumb ? '-17%' : 0,
                height: '100%', objectFit: 'cover',
                transition: 'opacity 0.25s',
                opacity: hovered ? 0.75 : 1,
              }}
            />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: '#bbb' }}>
              Sin imagen
            </div>
          )}
          {hovered && (
            <div style={{
              position: 'absolute', inset: 0,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'rgba(0,0,0,0.25)',
            }}>
              <EyeOutlined style={{ fontSize: 32, color: '#fff' }} />
            </div>
          )}
        </div>
      }
    >
      <div style={{ fontFamily: tipografia.body }}>
        <Text strong style={{ fontSize: 14, fontFamily: tipografia.title, display: 'block', marginBottom: 2 }}>
          {tituloTarjeta(animal, config)}
        </Text>
        {animal.corral && (
          <Tag
            style={{
              marginBottom: 4,
              fontSize: 11,
              background: config.color_primary || '#1a5f2a',
              color: '#fff',
              border: 'none',
              fontWeight: 600,
            }}
          >
            {terminoLote(config).nombre} {animal.corral}{animal._cantidadLote > 1 ? ` · ${animal._cantidadLote} cabezas` : ''}
          </Tag>
        )}
        {animal.nombre_categoria && (
          <Tag color="default" style={{ marginBottom: 4, fontSize: 11 }}>
            {animal.nombre_categoria}
          </Tag>
        )}
        {animal.nom_raza && (
          <Tag color="green" style={{ marginBottom: 6, fontSize: 11 }}>
            {animal.nom_raza}
          </Tag>
        )}
        {/* De qué cabaña VIENE el animal. Sólo tiene sentido cuando el remate
            tiene MÁS DE UNA: el backend completa `nom_cabana` siempre que el
            animal tenga `id_cabana`, así que sin `multiCabana` se pintaba el
            mismo tag en las 200 tarjetas de un remate de una sola cabaña. */}
        {multiCabana && animal.nom_cabana && (
          <Tag color="blue" style={{ marginBottom: 6, fontSize: 11 }}>
            {animal.nom_cabana}
          </Tag>
        )}
        {ofertasActivas && animal.precio_base && (
          // Con puja ya abierta la tarjeta dice «ABIERTO: $X» (la mejor pre-oferta
          // que manda el backend); sin ofertas, la base de siempre (2026-11-04).
          <div style={{ marginBottom: 6 }}>
            <DollarOutlined style={{ color: config.color_primary || '#1a5f2a', marginRight: 4 }} />
            {Number(animal.mejor_oferta) > 0 ? (
              <Text style={{ color: config.color_primary || '#1a5f2a', fontWeight: 700 }}>
                ABIERTO: ${Number(animal.mejor_oferta).toLocaleString('es-AR')}
              </Text>
            ) : (
              <Text style={{ color: config.color_primary || '#1a5f2a', fontWeight: 600 }}>
                Base: ${Number(animal.precio_base).toLocaleString('es-AR')}
              </Text>
            )}
          </div>
        )}
        {/* Acciones: "Ver ficha" alineado a la IZQUIERDA con su ancho natural
            (NO se expande). "Compartir WhatsApp" alineado a la DERECHA con
            estado normal solo icono + hover que expande a la izquierda
            mostrando el texto. Flex con space-between para separarlos. */}
        <div
          style={{
            marginTop: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <Button
            type="primary"
            style={{
              background: config.color_btn_primary || '#1a5f2a',
              border: 'none',
              fontSize: 14,
              borderRadius: 32,
              padding: '10px 20px',
              height: 'auto',
              lineHeight: 1,
            }}
            onClick={() => onVer(animal)}
          >
            Ver ficha
          </Button>
          <button
            type="button"
            className="puja-wa-share-btn"
            aria-label="Compartir por WhatsApp"
            onClick={() => onCompartir(animal)}
          >
            <WhatsAppOutlined />
            <span className="puja-wa-share-btn__label">Compartir</span>
          </button>
        </div>
      </div>
    </Card>
  );
};


// ── Catálogo con filtro lateral ───────────────────────────────────────
const CatalogoConFiltro = ({
  animales, animalesFiltrados, categorias, categoriaFiltro, setCategoriaFiltro,
  razaFiltro, setRazaFiltro, razas = [],
  cabanaFiltro, setCabanaFiltro, cabanas = [],
  config, tipografia, evento, spanPorColumna, ofertasActivas, onVer, onCompartir,
  sponsors,
}) => {
  const [busqueda, setBusqueda] = useState('');
  const [padreFiltro, setPadreFiltro] = useState(null);
  const [visibleCount, setVisibleCount] = useState(12);
  const filtroBarRef = useRef(null);
  const posicion = config.filtro_posicion || 'derecha';

  useEffect(() => { setVisibleCount(12); }, [categoriaFiltro, razaFiltro, cabanaFiltro, busqueda, padreFiltro]);
  const colorPrimary = config.color_primary || '#1a5f2a';

  // Hay algún filtro activo → mostramos el botón "Limpiar filtros" (UX: como
  // al filtrar seguimos en la ruta inicio, "Inicio"/logo no resetean el estado).
  const hayFiltros = !!(categoriaFiltro || razaFiltro || cabanaFiltro || busqueda.trim() || padreFiltro);
  const limpiarFiltros = () => {
    setCategoriaFiltro(null);
    setRazaFiltro(null);
    // Va junto a los demas: `hayFiltros` lo cuenta, asi que sin esta linea
    // filtrar por cabaña y apretar "Limpiar filtros" dejaba el boton en
    // pantalla y la grilla filtrada — un boton que no hace nada.
    setCabanaFiltro(null);
    setBusqueda('');
    setPadreFiltro(null);
  };

  // Animales con categoría + raza + búsqueda aplicados — base para derivar padres
  const animalesBase = useMemo(() => {
    let lista = animalesFiltrados;
    if (razaFiltro) lista = lista.filter(a => a.nom_raza === razaFiltro);
    // `nom_cabana_filtro`, no `nom_cabana`: hay que comparar contra el MISMO
    // campo que generó las opciones del Select (arriba), o el animal del
    // organizador —que tiene `nom_cabana` vacío a propósito— jamás matchearía
    // aunque el comprador elija justo esa opción.
    if (cabanaFiltro) lista = lista.filter(a => a.nom_cabana_filtro === cabanaFiltro);
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase();
      lista = lista.filter(a => textoBuscable(a).includes(q));
    }
    return lista;
  }, [animalesFiltrados, razaFiltro, cabanaFiltro, busqueda]);

  // Padres derivados de los animales filtrados (sin padre) — evita resultados vacíos
  const padres = useMemo(() => {
    const set = new Set();
    animalesBase.forEach(a => padresDe(a).forEach(p => set.add(p)));
    return Array.from(set).sort();
  }, [animalesBase]);

  // Si el padre seleccionado ya no existe en el filtro actual, lo limpiamos.
  // `padreFiltro` queda FUERA de las deps a proposito: solo hay que revisarlo
  // cuando cambia la lista de padres disponibles. Incluirlo re-dispararia el
  // efecto en cada seleccion, sin aportar nada.
  useEffect(() => {
    if (padreFiltro && !padres.includes(padreFiltro)) setPadreFiltro(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [padres]);

  // Aplicar filtro de padre sobre la base ya filtrada
  const animalesMostrados = useMemo(() => {
    if (!padreFiltro) return animalesBase;
    return animalesBase.filter(a => padresDe(a).includes(padreFiltro));
  }, [animalesBase, padreFiltro]);

  const animalesVisibles = animalesMostrados.slice(0, visibleCount);
  const hayMas = animalesMostrados.length > visibleCount;

  // Destacados: top 2 por vistas, o random si no hay vistas
  const destacados = useMemo(() => {
    const conVistas = [...animales].filter(a => a.vistas > 0).sort((a, b) => b.vistas - a.vistas);
    if (conVistas.length >= 2) return conVistas.slice(0, 2);
    const shuffled = [...animales].sort(() => Math.random() - 0.5);
    return shuffled.slice(0, 2);
  }, [animales]);

  const handleCategoriaChange = (val) => {
    setCategoriaFiltro(val || null);
    if (val) {
      analyticsService.track('visita_categoria', evento.id_evento, { id_categoria: val }).catch(() => {});  // telemetria fire-and-forget: no se le corta la navegacion a nadie por una metrica
    }
  };

  const sectionTitle = (icon, label) => (
    <div style={{ fontWeight: 700, fontSize: 11, textTransform: 'uppercase', color: '#999', marginBottom: 10, letterSpacing: 1, display: 'flex', alignItems: 'center', gap: 6 }}>
      {icon}{label}
    </div>
  );

  // LA `key` VA EN LA FIRMA, y por eso es el primer parametro: este helper
  // devuelve el `<div>` que se dibuja adentro de tres `.map()`, asi que sin
  // `key` React avisa por consola y cae a reconciliar por indice. Se escapo
  // porque el JSX esta detras de una llamada a funcion: el resto del archivo
  // pone `key` en todas sus listas, pero el linter de React no ve adentro de un
  // helper. Los items fijos ("Todas") pasan su propia clave literal.
  const itemFiltro = (key, activo, onClick, label) => (
    <div
      key={key}
      style={{
        padding: '5px 10px', borderRadius: 6, cursor: 'pointer', fontSize: 13,
        marginBottom: 3,
        background: activo ? colorPrimary : 'transparent',
        color: activo ? '#fff' : '#444',
        fontWeight: activo ? 600 : 400,
        transition: 'background 0.15s',
      }}
      onClick={onClick}
    >
      {label}
    </div>
  );

  const filtroMobileBar = (
    <div className="filtro-mobile-bar" ref={filtroBarRef}>
      <input
        type="text"
        placeholder="Buscar nombre o RP..."
        value={busqueda}
        onChange={e => setBusqueda(e.target.value)}
        style={{
          width: '100%', border: '1px solid #d9d9d9', borderRadius: 8,
          padding: '10px 14px', fontSize: 15, outline: 'none',
          fontFamily: tipografia.body, boxSizing: 'border-box',
          marginBottom: 10,
        }}
      />
      <div style={{ display: 'flex', gap: 8 }}>
        {categorias.length > 0 && (
          <Select
            allowClear
            placeholder="Categoría"
            style={{ flex: 1, minWidth: 0 }}
            value={categoriaFiltro}
            onChange={val => handleCategoriaChange(val || null)}
            popupMatchSelectWidth={false}
            getPopupContainer={() => filtroBarRef.current || document.body}
            dropdownStyle={{ width: filtroBarRef.current?.offsetWidth || '100%' }}
          >
            {categorias.map(c => (
              <Option key={c.id_categoria} value={c.id_categoria}>{c.nom_categoria}</Option>
            ))}
          </Select>
        )}
        {razas.length > 0 && (
          <Select
            allowClear
            placeholder="Raza"
            style={{ flex: 1, minWidth: 0 }}
            value={razaFiltro}
            onChange={val => setRazaFiltro(val || null)}
            popupMatchSelectWidth={false}
            getPopupContainer={() => filtroBarRef.current || document.body}
            dropdownStyle={{ width: filtroBarRef.current?.offsetWidth || '100%' }}
          >
            {razas.map(r => <Option key={r} value={r}>{r}</Option>)}
          </Select>
        )}
        {/* La lista se DERIVA del catálogo (el endpoint del micrositio no la
            manda; ver micrositio_routes.py:174). Se pide más de UNA porque con
            una sola el filtro no filtra nada: el backend completa `nom_cabana`
            siempre que el animal tenga `id_cabana`, aunque el remate sea de una
            sola cabaña. */}
        {cabanas.length > 1 && (
          <Select
            allowClear
            placeholder="Cabaña"
            style={{ flex: 1, minWidth: 0 }}
            value={cabanaFiltro}
            onChange={val => setCabanaFiltro(val || null)}
            popupMatchSelectWidth={false}
            getPopupContainer={() => filtroBarRef.current || document.body}
            dropdownStyle={{ width: filtroBarRef.current?.offsetWidth || '100%' }}
          >
            {cabanas.map(c => <Option key={c} value={c}>{c}</Option>)}
          </Select>
        )}
        {padres.length > 0 && (
          <Select
            allowClear
            placeholder="Padre"
            style={{ flex: 1, minWidth: 0 }}
            value={padreFiltro}
            onChange={val => setPadreFiltro(val || null)}
            popupMatchSelectWidth={false}
            getPopupContainer={() => filtroBarRef.current || document.body}
            dropdownStyle={{ width: filtroBarRef.current?.offsetWidth || '100%' }}
          >
            {padres.map(p => <Option key={p} value={p}>{p}</Option>)}
          </Select>
        )}
      </div>
    </div>
  );

  const filtroPanel = (
    <div style={{
      width: 210,
      flexShrink: 0,
      alignSelf: 'flex-start',
      fontFamily: tipografia.body,
      paddingTop: 0,
      display: 'flex',
      flexDirection: 'column',
      gap: 20,
    }}>

      {/* 1 — Buscador */}
      <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
        {sectionTitle(<SearchOutlined />, 'Buscar')}
        <input
          type="text"
          placeholder="Nombre o RP..."
          value={busqueda}
          onChange={e => setBusqueda(e.target.value)}
          style={{
            width: '100%', border: '1px solid #d9d9d9', borderRadius: 6,
            padding: '6px 10px', fontSize: 13, outline: 'none',
            fontFamily: tipografia.body, boxSizing: 'border-box',
          }}
        />
      </div>

      {/* 2 — Categorías */}
      {categorias.length > 0 && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
          {sectionTitle(<FilterOutlined />, 'Categorías')}
          {itemFiltro('todas', !categoriaFiltro, () => setCategoriaFiltro(null), 'Todas')}
          {categorias.map(c => itemFiltro(
            c.id_categoria,
            categoriaFiltro === c.id_categoria,
            () => handleCategoriaChange(c.id_categoria),
            c.nom_categoria
          ))}
        </div>
      )}

      {/* 3 — Razas */}
      {razas.length > 0 && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
          {sectionTitle(<span style={{ fontSize: 13 }}>🐄</span>, 'Por Raza')}
          {itemFiltro('todas', !razaFiltro, () => setRazaFiltro(null), 'Todas')}
          {razas.map(r => itemFiltro(
            r,
            razaFiltro === r,
            () => setRazaFiltro(r),
            r
          ))}
        </div>
      )}

      {/* 4 — Cabañas. Se pide más de UNA: con una sola el filtro no filtra
          nada (una única opción no sirve para elegir). `cabanas` sale de
          `nom_cabana_filtro`, que a diferencia de la etiqueta SÍ incluye al
          organizador siempre que tenga animales — ver el useMemo de arriba. */}
      {cabanas.length > 1 && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
          {sectionTitle(<BankOutlined />, 'Por Cabaña')}
          {itemFiltro('todas', !cabanaFiltro, () => setCabanaFiltro(null), 'Todas')}
          {cabanas.map(c => itemFiltro(
            c,
            cabanaFiltro === c,
            () => setCabanaFiltro(c),
            c
          ))}
        </div>
      )}

      {/* 5 — Padres */}
      {padres.length > 0 && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
          {sectionTitle(<span style={{ fontSize: 13 }}>🐂</span>, 'Por Padre')}
          <Select
            allowClear
            placeholder="Seleccionar padre"
            style={{ width: '100%', fontSize: 13 }}
            size="small"
            value={padreFiltro}
            onChange={val => setPadreFiltro(val || null)}
          >
            {padres.map(p => (
              <Option key={p} value={p}>{p}</Option>
            ))}
          </Select>
        </div>
      )}

      {/* 6 — Destacados */}
      {destacados.length > 0 && (
        <div style={{ background: '#fafafa', border: '1px solid #f0f0f0', borderRadius: 8, padding: '14px 12px' }}>
          {sectionTitle(<EyeOutlined />, 'Destacados')}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {destacados.map(animal => (
              <div
                key={animal.id_animal}
                style={{ cursor: 'pointer', borderRadius: 6, overflow: 'hidden', border: '1px solid #eee' }}
                onClick={() => onVer(animal)}
              >
                <DestacadoImg animal={animal} config={config} />
                <div style={{ padding: '6px 8px', background: '#fff' }}>
                  <div style={{ fontSize: 12, fontWeight: 600, color: '#333', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                    {tituloTarjeta(animal, config)}
                  </div>
                  {animal.corral && (
                    <div style={{ fontSize: 11, color: '#888' }}>{terminoLote(config).nombre} {animal.corral}</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

    </div>
  );

  const grilla = (
    <div style={{ flex: 1, minWidth: 0 }}>
      {hayFiltros && (
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          flexWrap: 'wrap', gap: 12, marginBottom: 20,
        }}>
          <span style={{ fontFamily: tipografia.body, color: '#666', fontSize: 14 }}>
            {animalesMostrados.length} {animalesMostrados.length === 1 ? 'resultado' : 'resultados'}
          </span>
          <Button
            icon={<CloseOutlined />}
            onClick={limpiarFiltros}
            style={{
              background: config.color_secondary || '#8B4513',
              color: '#fff', border: 'none', borderRadius: 32,
              fontFamily: tipografia.body, fontWeight: 600,
            }}
          >
            Limpiar filtros
          </Button>
        </div>
      )}
      {animalesMostrados.length === 0 ? (
        <Empty description="No hay animales con ese criterio" />
      ) : (
        <>
          <Row gutter={[16, 16]}>
            {animalesVisibles.map(animal => (
              <Col key={animal.id_animal} xs={24} sm={12} md={spanPorColumna > 6 ? 6 : spanPorColumna} lg={spanPorColumna}>
                <AnimalCard
                  animal={animal}
                  config={config}
                  tipografia={tipografia}
                  ofertasActivas={ofertasActivas}
                  onVer={onVer}
                  onCompartir={onCompartir}
                  multiCabana={cabanas.length > 1}
                />
              </Col>
            ))}
          </Row>
          {hayMas && (
            <div style={{ textAlign: 'center', marginTop: 28 }}>
              <Button
                size="large"
                onClick={() => setVisibleCount(c => c + 12)}
                style={{
                  background: colorPrimary, color: '#fff', border: 'none',
                  borderRadius: 8, fontFamily: tipografia.body, fontWeight: 600,
                  padding: '0 36px', height: 44,
                }}
              >
                Cargar más ({animalesMostrados.length - visibleCount} restantes)
              </Button>
            </div>
          )}
        </>
      )}

      {/* Sponsors debajo del catálogo, agrupados por nivel */}
      <SponsorsPorNivel sponsors={sponsors} tipografia={tipografia} config={config} />
    </div>
  );

  return (
    <div>
      {filtroMobileBar}
      <div style={{ display: 'flex', gap: 24, alignItems: 'flex-start' }}>
        {posicion === 'izquierda' && <div className="filtro-sidebar">{filtroPanel}</div>}
        {grilla}
        {posicion === 'derecha' && <div className="filtro-sidebar">{filtroPanel}</div>}
      </div>
    </div>
  );
};


// ── Footer del micrositio ─────────────────────────────────────────────
/**
 * @param {Array} [navPropia]  Reemplaza la columna "Navegación" y OCULTA la de
 *   "Información". Misma razón que en `SiteHeader`: los links de siempre
 *   —Contacto, Condiciones comerciales, sanitarias, Reclamos— apuntan a
 *   `/remate/:slug/...` y en el catálogo de genética esas rutas NO EXISTEN.
 *   Sin esto el pie de la vidriera son seis links a un 404.
 *
 *   Sin la prop dibuja lo de siempre, así que el micrositio de remate no
 *   cambia en nada.
 */
export const SiteFooter = ({
  config, tipografia, evento, categorias = [], logoUrl, onCategoriaSelect, slug,
  navPropia = null, rutaInicio = null,
}) => {
  const navigate = useNavigate();
  // `user` para rutear por rol, igual que en SiteHeader.
  const { isAuthenticated, user } = useContext(AuthContext);
  const numColumnas = parseInt(config.columnas_footer || '3', 10);
  const colorPrimary = config.color_primary || '#1a5f2a';
  const spanCol = Math.floor(24 / numColumnas);
  const catalogoPdf = config.catalogo_pdf_url ? fixUrl(config.catalogo_pdf_url) : null;

  // Fecha de finalización larga: "Jueves 9 de Julio de 2026".
  // El sufijo T00:00:00 fuerza parseo LOCAL y evita el corrimiento de día
  // que produce `new Date('YYYY-MM-DD')` (parsea en UTC).
  const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);
  const fmtFechaFin = (fechaStr) => {
    if (!fechaStr) return null;
    const d = new Date(`${fechaStr}T00:00:00`);
    const diaSemana = cap(d.toLocaleDateString('es-AR', { weekday: 'long' }));
    const mes = cap(d.toLocaleDateString('es-AR', { month: 'long' }));
    return `${diaSemana} ${d.getDate()} de ${mes} de ${d.getFullYear()}`;
  };

  const linkStyle = {
    color: 'rgba(255,255,255,0.75)',
    display: 'block',
    marginBottom: 6,
    fontSize: 13,
    cursor: 'pointer',
    textDecoration: 'none',
    fontFamily: tipografia.body,
    transition: 'color 0.2s',
  };

  const colTitleStyle = {
    color: '#fff',
    fontWeight: 700,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: 1,
    marginBottom: 12,
    fontFamily: tipografia.body,
  };

  // Columna 1: Logo + nombre del evento.
  // El logo va sin brightness/invert: ese filtro aplastaba a bloque blanco
  // cualquier logo que no fuera tipografico monocromo. La doble drop-shadow
  // (oscura + halo claro) sigue el alpha del PNG/SVG y separa el logo del
  // fondo cuando comparten color, sea el colorPrimary claro u oscuro.
  const col1 = (
    <Col xs={24} sm={12} md={spanCol} key="col1">
      {logoUrl && (
        <img src={fixUrl(logoUrl)} alt="Logo" style={{ maxHeight: 48, objectFit: 'contain', marginBottom: 12, display: 'block', filter: 'drop-shadow(0 1px 3px rgba(0,0,0,0.35)) drop-shadow(0 0 1px rgba(255,255,255,0.45))' }} />
      )}
      <div style={{ color: '#fff', fontWeight: 600, fontSize: 15, fontFamily: tipografia.title, marginBottom: 8 }}>
        {evento.nombre_evento}
      </div>
      <div style={{ color: 'rgba(255,255,255,0.6)', fontSize: 12 }}>
        {fmtFechaFin(evento.fecha_fin)}
      </div>
    </Col>
  );

  // Sub-bloques reusables.
  // A DONDE VA EL "INICIO" DEL PIE. Con navegacion propia scrolleaba al techo
  // de la pagina actual, y parado en la ficha de un animal eso NO es el inicio:
  // el comprador tocaba Inicio y se quedaba en el mismo animal, arriba. Es el
  // mismo bug que se arreglo en el logo, en el drawer y en layout2.
  //
  // La portada NO pasa `rutaInicio` a proposito: ahi el inicio ES esta pagina y
  // scrollear al techo es exactamente lo correcto. La ficha si lo pasa.
  const irAlInicioPie = () => (
    rutaInicio ? navigate(rutaInicio) : window.scrollTo({ top: 0, behavior: 'smooth' })
  );

  const navegacionBlock = navPropia ? (
    <>
      <div style={colTitleStyle}>Navegación</div>
      <span style={linkStyle} onClick={irAlInicioPie}>Inicio</span>
      {/* Sólo los items PLANOS: un desplegable no tiene dónde abrirse en un
          pie de página, así que sus hijos irían a parar a una lista larguísima
          de nombres de toros. */}
      {navPropia.filter((i) => !i.items).map((item) => (
        <span key={item.key} style={linkStyle} onClick={item.onClick}>{item.label}</span>
      ))}
    </>
  ) : (
    <>
      <div style={colTitleStyle}>Navegación</div>
      <span style={linkStyle} onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>Inicio</span>
      <span style={linkStyle} onClick={() => document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' })}>Catálogo</span>
      {catalogoPdf && <span style={linkStyle} onClick={() => abrirExterna(catalogoPdf)}>Catálogo PDF</span>}
      <span style={linkStyle} onClick={() => slug && navigate(`/remate/${slug}/contacto`)}>Contacto</span>
      <span style={linkStyle} onClick={() => navigate(rutaAcceso(isAuthenticated, user, slug))}>
        {isAuthenticated ? 'Mi Panel' : 'Acceder'}
      </span>
    </>
  );

  // Con navegación propia NO hay bloque de Información: sus tres links son de
  // un remate (condiciones comerciales, sanitarias, reclamos) y en el catálogo
  // no existen esas páginas.
  const informacionBlock = navPropia ? null : (
    <>
      <div style={colTitleStyle}>Información</div>
      <span style={linkStyle} onClick={() => slug && navigate(`/remate/${slug}/condiciones-comerciales`)}>Condiciones comerciales</span>
      <span style={linkStyle} onClick={() => slug && navigate(`/remate/${slug}/condiciones-sanitarias`)}>Condiciones sanitarias</span>
      <span style={linkStyle} onClick={() => slug && navigate(`/remate/${slug}/reclamos-garantias`)}>Reclamos y garantías</span>
    </>
  );

  // Columna 2: Navegación. Con 3 columnas, Información se apila debajo
  // de Navegación (decisión UX 2026-06-24: la 4ta columna desaparece pero
  // su contenido no se pierde).
  const col2 = (
    <Col xs={24} sm={12} md={spanCol} key="col2">
      {navegacionBlock}
      {numColumnas === 3 && (
        <div style={{ marginTop: 24 }}>{informacionBlock}</div>
      )}
    </Col>
  );

  // Columna 3: Categorías.
  //
  // SIN CATEGORIAS NO VA NI EL TITULO. Las dos paginas del catalogo pasan
  // `categorias={[]}` explicito, asi que ahi quedaba un "CATEGORIAS" en
  // mayusculas con NADA debajo — con `columnas_footer: 3`, un tercio del pie
  // ocupado por un rotulo vacio. Mismo criterio que `informacionBlock`, y que
  // el que este archivo repite dos veces: un item que baja a una seccion vacia
  // es el mismo link muerto que se estaba tratando de sacar.
  //
  // El `= []` de la firma se agrego JUNTO con esta guarda: no estaba, y
  // `categorias.length` sobre `undefined` es un TypeError, o sea micrositio
  // publico en blanco. Los diez llamadores la pasan hoy; alcanzaba uno que no.
  const col3 = categorias.length === 0 ? null : (
    <Col xs={24} sm={12} md={spanCol} key="col3">
      <div style={colTitleStyle}>Categorías</div>
      {categorias.map(c => (
        <span
          key={c.id_categoria}
          style={linkStyle}
          onClick={() => {
            onCategoriaSelect(c.id_categoria);
            document.getElementById('catalogo')?.scrollIntoView({ behavior: 'smooth' });
          }}
        >
          {c.nom_categoria}
        </span>
      ))}
    </Col>
  );

  // Columna 4 (solo si columnas_footer = 4): Información standalone.
  //
  // MISMO CRITERIO QUE col3, y esto se habia escapado en el commit que arreglo
  // col3 — dos lineas mas abajo, con el comentario ya puesto. Con navegacion
  // propia `informacionBlock` es null POR DISEÑO, pero `col4` no era null: era
  // un `<Col>` con un null adentro, asi que sobrevivia al `.filter(Boolean)` y
  // dejaba un cuarto del pie vacio. Es alcanzable: el tab de Estructura ofrece
  // "4 Columnas" y el productor la elige.
  const col4 = informacionBlock ? (
    <Col xs={24} sm={12} md={spanCol} key="col4">
      {informacionBlock}
    </Col>
  ) : null;

  // `.filter(Boolean)`: `col3` puede venir null, y un `<Col>` nulo en el array
  // no rompe pero deja su hueco en la grilla y descentra las que quedan.
  const columnas = (numColumnas === 4
    ? [col1, col2, col3, col4]
    : [col1, col2, col3]).filter(Boolean);

  return (
    <footer style={{ fontFamily: tipografia.body }}>
      {/* Footer del productor (color primary del micrositio) */}
      <div style={{ background: colorPrimary }}>
        <div style={{ maxWidth: 1400, margin: '0 auto', padding: '48px 24px 24px' }}>
          <Row gutter={[32, 32]}>
            {columnas}
          </Row>
        </div>
      </div>

      {/* Brand bar PUJA - siempre presente, espacio de marca de la plataforma.
          Negro grafito + wordmark PUJA en Mont blanco + bajada en dorado. */}
      <div
        style={{
          background: '#1f1f1f',
          padding: '14px 24px',
          textAlign: 'center',
        }}
      >
        <div
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 12,
            color: '#fff',
          }}
        >
          <span
            style={{
              fontFamily: "'Mont', sans-serif",
              fontWeight: 900,
              fontSize: 18,
              letterSpacing: '0.5px',
              color: '#fff',
            }}
          >
            PUJA
          </span>
          <span style={{ color: 'rgba(255,255,255,0.3)', fontSize: 14 }}>|</span>
          <span
            style={{
              color: '#b4986d',
              fontSize: 13,
              fontWeight: 900,
              fontFamily: tipografia.body,
              textTransform: 'uppercase',
              letterSpacing: '0.8px',
            }}
          >
            Gestión de cabañas
          </span>
        </div>
      </div>
    </footer>
  );
};


export default RematePage;
